/**
 * WHETHER THIS CUSTOMER GETS CURE SHEETS AT ALL.
 *
 * Cure sheets are subscription VALUE, not a free feature, so the list and the
 * viewer are both gated. This module is the ONE place that decides, so the nav
 * item, the route and the screen cannot disagree — a nav entry that leads to a
 * "not entitled" page is a worse experience than no nav entry.
 *
 * ================== RESOLVED: THE SERVER DECIDES ==================
 * Joel ruled 2026-09-06: a company has cure-sheet access iff AT LEAST ONE of its
 * devices is subscribed. That is resolved SERVER-SIDE and returned on the list
 * response as `companyHasCureSheetAccess`, alongside a list that is itself gated.
 *
 * It has to be server-side. Entitlement was defined per-DEVICE, resolved from a
 * serial, and a console user has no serial — so the client cannot evaluate the
 * rule even in principle. Anything the client computed would be a second,
 * unaudited definition that drifts from the one the field units obey.
 *
 * So this module no longer DECIDES anything; it only refuses to treat silence as
 * a yes. The single seam is `isEntitledToCureSheets`, and the only source that
 * can grant access is 'server'.
 * ===================================================================
 */

/**
 * Where the answer came from. Named so 'unknown' cannot pass silently.
 *
 * 'unknown' is a real, expected state, not just an initial one: a server that has
 * not yet shipped the flag omits it, and that is indistinguishable from "no
 * access". It fails CLOSED — the recoverable mistake is a customer asking where
 * the feature went, not giving away the thing the subscription is for.
 */
export type EntitlementSource = 'unknown' | 'server';

export interface CureSheetEntitlement {
  source: EntitlementSource;
  entitled: boolean;
}

/** What the app holds before anything has answered. */
export const ENTITLEMENT_UNKNOWN: CureSheetEntitlement = {
  source: 'unknown',
  entitled: false,
};

/** Convenience for specs and for a server that answered plainly. */
export function serverEntitlement(entitled: boolean): CureSheetEntitlement {
  return { source: 'server', entitled };
}

/**
 * True only when something actually said yes.
 *
 * An unknown entitlement is NOT a yes. Written as an explicit source check
 * rather than `!!e?.entitled` so that adding a future source cannot accidentally
 * default to permitting.
 */
export function isEntitledToCureSheets(entitlement: CureSheetEntitlement | null | undefined): boolean {
  if (!entitlement) return false;
  if (entitlement.source === 'unknown') return false;
  return entitlement.entitled === true;
}
